import { isDefined, isNotDefined } from '@sgme/fp';
import { Spacer, VStack } from '@sgme/ui';
import deepEqual from 'fast-deep-equal';
import { memo, useCallback } from 'react';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useCallOrderIsChecking,
  useCallOrderIsModifyingField,
  useCallOrderTileId,
  useValidateCallOrder,
} from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderContext.tsx';
import { CallOrderExpiry } from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderExpiry.tsx';
import { CallOrderLimitPrice } from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderLimitPrice.tsx';
import { ConnectedCallOrderSpot } from '@/App/tiles/orders/CallOrder/ConnectedCallOrderSpot.tsx';
import { OrderFooter } from '@/App/tiles/orders/components/OrderFooter.tsx';
import { OrderHeader } from '@/App/tiles/orders/components/OrderHeader.tsx';
import { OrderNotifications } from '@/App/tiles/orders/components/OrderNotifications.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useSubmitCallOrderMutation } from '@/store/orders/call/api/index.ts';
import {
  selectCallOrderErrors,
  selectCallOrderTile,
  selectNotificationEmailsCallOrder,
} from '@/store/orders/call/selectors.ts';
import type { ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

/**
 * Memoized CallOrderCreationTile component because the Grid rerenders a lot.
 */
export const CallOrderCreationTile = memo(() => {
  const tileId = useCallOrderTileId();
  const isModifyingField = useCallOrderIsModifyingField();
  const dispatch = useAppDispatch();
  const track = useTrackEvent();

  const notificationEmails = useAppSelector((state) => selectNotificationEmailsCallOrder(state, tileId));
  const errors = useAppSelector((state) => selectCallOrderErrors(state, tileId));
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));

  const validate = useValidateCallOrder();
  const [submitCallOrder] = useSubmitCallOrderMutation({
    selectFromResult: () => ({}),
  });

  const isChecking = useCallOrderIsChecking();

  const { areFieldsVisible, tile } = useAppSelector((state) => {
    const tile = selectCallOrderTile(state, tileId);

    return {
      tile,
      areFieldsVisible: isDefined(tile?.properties.currencyPair),
    };
  }, deepEqual);

  const handleSubmit = useCallback(() => {
    track('Order action', 'Submit Order');
    if (isNotDefined(tile)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    if (isNotDefined(clientId)) {
      console.error('[handleSubmit] client id is not defined');
      return;
    }

    if (isNotDefined(notificationEmails)) {
      console.error('[handleSubmit] Notification emails not found in the tile or user preferences.');
      return;
    }

    if (isNotDefined(tile.properties.currencyPair) || isNotDefined(tile.properties.limitPrice)) {
      console.error('[handleSubmit] Currency pair or limit price is not defined in the tile.');
      return;
    }

    submitCallOrder({
      tileId,
      currency1: tile.properties.currencyPair.currency1,
      currency2: tile.properties.currencyPair.currency2,
      clientId,
      limitPrice: tile.properties.limitPrice,
      expiry: tile.properties.expiry,
      notificationEmails: tile.properties.notificationEmails ?? [],
      clockOffset: tile.properties.clockOffset,
    })
      .unwrap()
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to submit call order:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              /**
               * send 'g10' or 'emerging' on order creation when we are able to differentiate
               */
              orderCurrencyType: 'unknown',
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  }, [tileId, clientId, notificationEmails, tile, submitCallOrder]);

  return (
    <AutoAdvanceForm className="d-flex w-100 h-100">
      <VStack className="bg-lvl1 py-2" fullWidth fullHeight>
        <OrderHeader orderType="CallOrder" tile={tile} tileId={tileId} validate={validate} />

        {areFieldsVisible && (
          <>
            <div
              className="mx-16px"
              style={{
                display: 'grid',
                gap: '5px',
                gridTemplateColumns: 'auto minmax(0, 1fr)',
                gridTemplateRows: '20px',
                alignItems: 'center',
              }}
            >
              <ConnectedCallOrderSpot />
              <CallOrderLimitPrice />
              <CallOrderExpiry />
              <OrderNotifications
                tileId={tileId}
                errors={errors}
                notificationEmails={notificationEmails}
                validate={validate}
              />
            </div>

            <Spacer />

            <OrderFooter tile={tile} isModifyingField={isModifyingField} handleSubmit={handleSubmit} />
          </>
        )}
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
});
